import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product, GiftCodeValidation } from '../types';

interface CartState {
  items: CartItem[];
  giftCode: GiftCodeValidation | null;
  customerNote: string;
  deliveryFee: number;
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  setGiftCode: (giftCode: GiftCodeValidation | null) => void;
  setCustomerNote: (note: string) => void;
  setDeliveryFee: (fee: number) => void;
  totalItems: () => number;
  subtotal: () => number;
  discount: () => number;
  finalTotal: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      giftCode: null,
      customerNote: '',
      deliveryFee: 0,

      addItem: (product, quantity = 1) => {
        set((state) => {
          const existing = state.items.find((i) => i.productId === product.id);
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.productId === product.id
                  ? {
                      ...i,
                      quantity: i.quantity + quantity,
                      subtotal: (i.quantity + quantity) * i.unitPrice,
                    }
                  : i
              ),
            };
          }
          return {
            items: [
              ...state.items,
              {
                productId: product.id,
                name: product.name,
                quantity,
                unitPrice: product.price,
                subtotal: product.price * quantity,
                imageUrl: product.imageUrl,
              },
            ],
          };
        });
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId
              ? { ...i, quantity, subtotal: quantity * i.unitPrice }
              : i
          ),
        }));
      },

      clearCart: () => set({ items: [], giftCode: null, customerNote: '', deliveryFee: 0 }),

      setGiftCode: (giftCode) => set({ giftCode }),

      setCustomerNote: (customerNote) => set({ customerNote }),

      setDeliveryFee: (deliveryFee) => set({ deliveryFee }),

      totalItems: () => {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },

      subtotal: () => {
        return get().items.reduce((sum, i) => sum + i.subtotal, 0);
      },

      discount: () => {
        const gift = get().giftCode;
        const subtotal = get().subtotal();
        if (!gift) return 0;
        return Math.min(gift.discountAmount, subtotal);
      },

      finalTotal: () => {
        return Math.max(0, get().subtotal() - get().discount() + get().deliveryFee);
      },
    }),
    {
      name: 'x1493-cart',
      partialize: (state) => ({ items: state.items, giftCode: state.giftCode, customerNote: state.customerNote }),
    }
  )
);
